import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
            try {
                const { state } = JSON.parse(authStorage);
                // Check for accessToken (new format) or token (legacy)
                const token = state?.accessToken || state?.token;
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch (e) {
                // Invalid JSON in storage
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('auth-storage');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// API helper functions
export const projectsApi = {
    list: () => api.get('/projects'),
    get: (id: string) => api.get(`/projects/${id}`),
    create: (data: any) => api.post('/projects', data),
    update: (id: string, data: any) => api.put(`/projects/${id}`, data),
    delete: (id: string) => api.delete(`/projects/${id}`),
    toggleModule: (projectId: string, moduleType: string) =>
        api.post(`/projects/${projectId}/modules/${moduleType}/toggle`),
};

export const seoApi = {
    audit: (projectId: string, url?: string) =>
        api.post('/seo/audit', { projectId, url }),
    getAudit: (auditId: string) => api.get(`/seo/audit/${auditId}`),
    listAudits: (projectId: string) => api.get(`/seo/audits/${projectId}`),

    researchKeywords: (projectId: string, seedKeywords: string[]) =>
        api.post('/seo/keywords/research', { projectId, seedKeywords }),
    getKeywords: (projectId: string) => api.get(`/seo/keywords/${projectId}`),

    generateContent: (projectId: string, data: any) =>
        api.post('/seo/content/generate', { projectId, ...data }),
    getContent: (projectId: string) => api.get(`/seo/content/${projectId}`),
};

export const socialApi = {
    createPost: (data: any) => api.post('/social/posts', data),
    generatePost: (projectId: string, data: any) =>
        api.post('/social/posts/generate', { projectId, ...data }),
    listPosts: (projectId: string) => api.get(`/social/posts/${projectId}`),
    publishPost: (postId: string) => api.post(`/social/posts/${postId}/publish`),
    deletePost: (postId: string) => api.delete(`/social/posts/${postId}`),
    // New endpoints
    getAnalytics: (projectId: string) => api.get(`/social/analytics/${projectId}`),
    suggestHashtags: (topic: string, platform?: string, count?: number) =>
        api.post('/social/hashtags/suggest', { topic, platform, count }),
    schedulePost: (postId: string, scheduledAt: string) =>
        api.post(`/social/posts/${postId}/schedule`, { scheduledAt }),
};

export const adsApi = {
    createCampaign: (data: any) => api.post('/ads/campaigns', data),
    listCampaigns: (projectId: string) => api.get(`/ads/campaigns/${projectId}`),
    getCampaign: (id: string) => api.get(`/ads/campaigns/item/${id}`),
    updateCampaign: (id: string, data: any) => api.put(`/ads/campaigns/${id}`, data),
    activateCampaign: (id: string) => api.post(`/ads/campaigns/${id}/activate`),
    pauseCampaign: (id: string) => api.post(`/ads/campaigns/${id}/pause`),
    getOptimizationSuggestions: (campaignId: string) =>
        api.post('/ads/optimization/suggest', { campaignId }),
    // New endpoints
    generateAdCopy: (data: { productName: string; productDescription?: string; platform: string; objective: string; tone?: string }) =>
        api.post('/ads/generate-copy', data),
    getCampaignAnalytics: (campaignId: string) =>
        api.get(`/ads/campaigns/${campaignId}/analytics`),
    bulkAction: (campaignIds: string[], action: 'activate' | 'pause' | 'delete') =>
        api.post('/ads/campaigns/bulk-action', { campaignIds, action }),
};

export const emailApi = {
    createCampaign: (data: any) => api.post('/email/campaigns', data),
    generateCampaign: (projectId: string, data: any) =>
        api.post('/email/campaigns/generate', { projectId, ...data }),
    listCampaigns: (projectId: string) => api.get(`/email/campaigns/${projectId}`),
    getCampaign: (id: string) => api.get(`/email/campaigns/item/${id}`),
    updateCampaign: (id: string, data: any) => api.put(`/email/campaigns/${id}`, data),
    deleteCampaign: (id: string) => api.delete(`/email/campaigns/${id}`),
    sendCampaign: (id: string) => api.post(`/email/campaigns/${id}/send`),
    // New endpoints
    getCampaignAnalytics: (campaignId: string) =>
        api.get(`/email/campaigns/${campaignId}/analytics`),
    generateSubjectLines: (topic: string, tone?: string, currentSubject?: string) =>
        api.post('/email/campaigns/generate-subjects', { topic, tone, currentSubject }),
    previewCampaign: (campaignId: string) =>
        api.post(`/email/campaigns/${campaignId}/preview`),
    cloneCampaign: (campaignId: string) =>
        api.post(`/email/campaigns/${campaignId}/clone`),
    scheduleCampaign: (campaignId: string, scheduledAt: string) =>
        api.post(`/email/campaigns/${campaignId}/schedule`, { scheduledAt }),
};

export const paymentsApi = {
    getDadyPayBalance: (projectId: string) =>
        api.get(`/payments/dadypay/balance/${projectId}`),
    createPayout: (data: any) => api.post('/payments/dadypay/payout', data),
    getCryptoBalance: (projectId: string) =>
        api.get(`/payments/crypto/balance/${projectId}`),
    listTransactions: (projectId: string) =>
        api.get(`/payments/transactions/${projectId}`),
};

export const analyticsApi = {
    getDashboard: (projectId: string) =>
        api.get(`/analytics/dashboard/${projectId}`),
    generateReport: (projectId: string, data: any) =>
        api.post('/analytics/reports', { projectId, ...data }),
    listReports: (projectId: string) => api.get(`/analytics/reports/${projectId}`),
    getReport: (reportId: string) => api.get(`/analytics/reports/item/${reportId}`),
    downloadReport: (reportId: string) => api.get(`/analytics/reports/download/${reportId}`, { responseType: 'blob' }),
    getInsights: (projectId: string, focus?: string) =>
        api.post('/analytics/insights', { projectId, focus }),
    // New endpoints
    getModuleAnalytics: (module: 'seo' | 'social' | 'ads' | 'email', projectId: string) =>
        api.get(`/analytics/module/${module}/${projectId}`),
    comparePeriods: (projectId: string, period1Start: string, period1End: string, period2Start: string, period2End: string) =>
        api.post('/analytics/compare', { projectId, period1Start, period1End, period2Start, period2End }),
    exportData: (projectId: string, format?: 'json' | 'csv', modules?: string[]) =>
        api.post('/analytics/export', { projectId, format, modules }),
};

export const orchestratorApi = {
    createAutomation: (data: any) => api.post('/orchestrator/automations', data),
    listAutomations: (projectId: string) =>
        api.get(`/orchestrator/automations/${projectId}`),
    toggleAutomation: (id: string) =>
        api.post(`/orchestrator/automations/${id}/toggle`),
    getDecision: (projectId: string, context: any, question: string) =>
        api.post('/orchestrator/decide', { projectId, context, question }),
    getHealth: () => api.get('/orchestrator/health'),
};

// Organization & Team Management
export const organizationApi = {
    get: () => api.get('/organization'),
    update: (data: any) => api.put('/organization', data),
    invite: (email: string, role: string) =>
        api.post('/organization/invite', { email, role }),
    acceptInvite: (token: string) =>
        api.post('/organization/invite/accept', { token }),
    removeMember: (userId: string) =>
        api.delete(`/organization/members/${userId}`),
    updateMemberRole: (userId: string, role: string) =>
        api.put(`/organization/members/${userId}`, { role }),
    getUsage: () => api.get('/organization/usage'),
    // GDPR endpoints
    exportData: () => api.get('/organization/data-export'),
    requestDeletion: (confirmEmail: string, reason?: string) =>
        api.post('/organization/data-delete', { confirmEmail, reason }),
    cancelDeletion: () => api.post('/organization/data-delete/cancel'),
    getAuditLog: () => api.get('/organization/data-audit'),
    // DadyPay credentials management
    getDadyPayStatus: () => api.get('/organization/dadypay'),
    updateDadyPay: (data: { apiKey?: string; secretKey?: string; webhookSecret?: string; enabled?: boolean }) =>
        api.put('/organization/dadypay', data),
    removeDadyPay: () => api.delete('/organization/dadypay'),
    testDadyPay: () => api.post('/organization/dadypay/test'),
};

// MCP API Key Management
export const apiKeysApi = {
    list: () => api.get('/organization/api-keys'),
    create: (name: string, permissions?: string[]) =>
        api.post('/organization/api-keys', { name, permissions }),
    revoke: (keyId: string) => api.delete(`/organization/api-keys/${keyId}`),
};

// AI Provider API Keys Management
export const aiKeysApi = {
    get: () => api.get('/organization/ai-keys'),
    update: (keys: { openai?: string; anthropic?: string; google?: string; xai?: string; usePlatformKey?: boolean }) =>
        api.put('/organization/ai-keys', keys),
    remove: (provider: 'openai' | 'anthropic' | 'google' | 'xai') =>
        api.delete(`/organization/ai-keys/${provider}`),
};

// Modules & Add-ons API (DadyPay powered)
export const modulesApi = {
    // Catalog
    getCatalog: () => api.get('/modules/catalog'),
    getMyModules: () => api.get('/modules/my-modules'),
    checkAccess: (moduleType: string) => api.get(`/modules/check/${moduleType}`),

    // Trials & Purchases (DadyPay crypto)
    startTrial: (moduleType: string) =>
        api.post('/modules/start-trial', { moduleType }),
    purchaseModule: (moduleType: string, billingPeriod: 'monthly' | 'yearly' = 'monthly') =>
        api.post('/modules/purchase', { moduleType, billingPeriod }),
    purchase: (moduleType: string, billingPeriod: 'monthly' | 'yearly' = 'monthly') =>
        api.post('/modules/purchase', { moduleType, billingPeriod }), // Alias
    purchaseBundle: (bundleId: string, billingPeriod: 'monthly' | 'yearly' = 'monthly') =>
        api.post('/modules/purchase-bundle', { bundleId, billingPeriod }),
    cancelModule: (moduleType: string) =>
        api.post('/modules/cancel', { moduleType }),

    // Payment status
    getPaymentStatus: (transactionId: string) =>
        api.get(`/modules/payment-status/${transactionId}`),

    // Promo codes
    applyPromo: (code: string) =>
        api.post('/modules/apply-promo', { code }),

    // Usage
    getUsage: (startDate?: string, endDate?: string, moduleType?: string) =>
        api.get('/modules/usage', { params: { startDate, endDate, moduleType } }),
};

// Auth endpoints
export const authApi = {
    login: (email: string, password: string) =>
        api.post('/auth/login', { email, password }),
    register: (email: string, password: string, name: string) =>
        api.post('/auth/register', { email, password, name }),
    logout: (refreshToken?: string) =>
        api.post('/auth/logout', { refreshToken }),
    refresh: (refreshToken: string) =>
        api.post('/auth/refresh', { refreshToken }),
    me: () => api.get('/auth/me'),
    requestPasswordReset: (email: string) =>
        api.post('/auth/password-reset/request', { email }),
    confirmPasswordReset: (token: string, newPassword: string) =>
        api.post('/auth/password-reset/confirm', { token, newPassword }),
};
